'use client';

import dynamic from 'next/dynamic';
import Link from '~/(tenant)/_components/tenant-link';

import { useQuery } from '@tanstack/react-query';

import { PersonalAccountDropdown } from '@kit/accounts/personal-account-dropdown';
import { useSignOut } from '@kit/supabase/hooks/use-sign-out';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { JWTUserData } from '@kit/supabase/types';
import { If } from '@kit/ui/if';
import { Trans } from '@kit/ui/trans';

import { UserAccountDropdown } from '~/(tenant)/_components/user-account-dropdown';
import featuresFlagConfig from '~/config/feature-flags.config';
import pathsConfig from '~/config/paths.config';

const ModeToggle = dynamic(() =>
  import('@kit/ui/mode-toggle').then((mod) => ({
    default: mod.ModeToggle,
  })),
);

const MobileModeToggle = dynamic(() =>
  import('@kit/ui/mobile-mode-toggle').then((mod) => ({
    default: mod.MobileModeToggle,
  })),
);

const paths = {
  home: pathsConfig.app.home,
  bookCourt: '/book',
};

const features = {
  enableThemeToggle: featuresFlagConfig.enableThemeToggle,
};

interface NetAcademyHeaderAccountSectionProps {
  onNavigate?: () => void;
}

export function NetAcademyHeaderAccountSection({
  onNavigate,
}: NetAcademyHeaderAccountSectionProps) {
  const session = useSession();
  const userRole = useUserRole(session.data?.user?.id);
  const teamAccount = useTeamAccount(session.data?.user?.id);
  const signOut = useSignOut({
    onSuccess: () => window.location.reload(),
  });

  if (session.data) {
    const role = userRole.data || 'guest';

    if (role === 'owner' || role === 'member') {
      return (
        <div className="animate-in fade-in flex items-center gap-x-3 duration-500">
          <div className="hidden md:flex">
            <If condition={features.enableThemeToggle}>
              <ModeToggle />
            </If>
          </div>

          <Link
            href={paths.bookCourt}
            onClick={onNavigate}
            className="inline-flex h-10 items-center justify-center rounded-full bg-[var(--net-lime)] px-6 text-sm font-bold text-[var(--net-ink)] transition-transform duration-200 hover:scale-[1.03] active:scale-[0.97]"
          >
            <Trans i18nKey="common:bookCourt" defaults="Book Now" />
          </Link>

          <Link
            href={
              teamAccount.data
                ? `/home/${teamAccount.data.slug}/dashboard`
                : '/home/settings'
            }
            onClick={onNavigate}
            className="hidden h-10 items-center justify-center rounded-full border border-white/40 px-5 text-sm font-medium text-white transition-colors duration-200 hover:bg-white/10 md:inline-flex"
          >
            <Trans i18nKey="common:adminPanel" defaults="Admin Panel" />
          </Link>

          <PersonalAccountDropdown
            showProfileName={false}
            paths={paths}
            features={features}
            user={session.data.user as unknown as JWTUserData}
            signOutRequested={() => signOut.mutateAsync()}
            className="z-50"
          />
        </div>
      );
    }

    if (role === 'user') {
      return (
        <div className="animate-in fade-in flex items-center gap-x-3 duration-500">
          <div className="hidden md:flex">
            <If condition={features.enableThemeToggle}>
              <ModeToggle />
            </If>
          </div>

          <Link
            href={paths.bookCourt}
            onClick={onNavigate}
            className="inline-flex h-10 items-center justify-center rounded-full bg-[var(--net-lime)] px-6 text-sm font-bold text-[var(--net-ink)] transition-transform duration-200 hover:scale-[1.03] active:scale-[0.97]"
          >
            <Trans i18nKey="common:bookCourt" defaults="Book Now" />
          </Link>

          <UserAccountDropdown
            showProfileName={false}
            features={features}
            user={session.data.user as unknown as JWTUserData}
            signOutRequested={() => signOut.mutateAsync()}
            className="z-50"
          />
        </div>
      );
    }
  }

  return <AuthButtons onNavigate={onNavigate} />;
}

function AuthButtons({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <div className="animate-in fade-in flex items-center gap-x-3 duration-500">
      <div className="hidden md:flex">
        <If condition={features.enableThemeToggle}>
          <ModeToggle />
        </If>
      </div>

      <div className="md:hidden">
        <If condition={features.enableThemeToggle}>
          <MobileModeToggle />
        </If>
      </div>

      <Link
        href={paths.bookCourt}
        onClick={onNavigate}
        className="inline-flex h-10 items-center justify-center rounded-full bg-[var(--net-lime)] px-6 text-sm font-bold text-[var(--net-ink)] transition-transform duration-200 hover:scale-[1.03] active:scale-[0.97]"
      >
        <Trans i18nKey="common:bookCourt" defaults="Book Now" />
      </Link>

      {/* Hard navigation to avoid hydration mismatch between route groups */}
      <a
        href={pathsConfig.auth.signIn}
        onClick={onNavigate}
        className="inline-flex h-10 items-center justify-center rounded-full border border-white/40 px-5 text-sm font-medium text-white transition-colors duration-200 hover:bg-white/10"
      >
        <Trans i18nKey="auth:signIn" defaults="Sign In" />
      </a>
    </div>
  );
}

function useSession() {
  const client = useSupabase();

  return useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data } = await client.auth.getSession();
      return data.session;
    },
  });
}

function useUserRole(userId?: string) {
  const client = useSupabase();

  return useQuery({
    queryKey: ['user-role', userId],
    queryFn: async () => {
      if (!userId) return 'guest';

      try {
        const { data: memberships } = await client
          .from('accounts_memberships')
          .select('account_role')
          .eq('user_id', userId);

        if (memberships && memberships.length > 0) {
          if (memberships.some((m) => m.account_role === 'owner')) {
            return 'owner';
          }
          return 'member';
        }

        return 'user';
      } catch {
        return 'guest';
      }
    },
    enabled: !!userId,
  });
}

function useTeamAccount(userId?: string) {
  const client = useSupabase();

  return useQuery({
    queryKey: ['team-account', userId],
    queryFn: async () => {
      if (!userId) return null;

      try {
        const { data: memberships } = await client
          .from('accounts_memberships')
          .select(
            `
            account_id,
            accounts!accounts_memberships_account_id_fkey!inner (
              id,
              slug,
              name,
              is_personal_account
            )
          `,
          )
          .eq('user_id', userId)
          .eq('accounts.is_personal_account', false);

        if (memberships && memberships.length > 0) {
          return memberships[0]?.accounts;
        }

        return null;
      } catch {
        return null;
      }
    },
    enabled: !!userId,
  });
}
